import AsyncStorage from "@react-native-async-storage/async-storage";
import { attachSignedFileUrls } from "helpers/shared.helpers";
import { decodeJwt } from "jose";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import AuthService from "services/Auth.service";
import PersonService from "services/Person.service";
import { ROLE_ADMIN, ROLE_MESSAGING_MANAGER } from "web/constants";
import { ToastContext } from "webContext/Toast.context";

export const AuthContext = createContext();

const defaultUserInfo = {
  firstName: "",
  lastName: "",
  email: "",
};

export const AuthProvider = ({ children }) => {
  const { showToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [userInfo, setUserInfo] = useState(defaultUserInfo);
  const [authInitializing, setAuthInitializing] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const savedToken = await AsyncStorage.getItem("userToken");
        if (savedToken) {
          const decodedInfo = decodeJwt(savedToken);
          const res = await PersonService.getPersonInfo(decodedInfo.personId);
          const resUpdated = await attachSignedFileUrls(res.payload[0]);
          setUserInfo(resUpdated);
          setUserToken(savedToken);
        } else {
          setUserToken(null);
          setUserInfo(defaultUserInfo);
        }
      } catch {
        setUserToken(null);
        await AsyncStorage.removeItem("userToken");
        setUserInfo(defaultUserInfo);
      } finally {
        setAuthInitializing(false);
      }
    }

    fetchData();
  }, []);

  const login = useCallback(
    async (email, password, organizationId) => {
      setIsLoading(true);
      try {
        const res = await AuthService.login(email, password, organizationId);
        setUserToken(res.accessToken);
        await AsyncStorage.setItem("userToken", res.accessToken);
        setUserInfo(await attachSignedFileUrls(res.userInfo));
      } catch (error) {
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "An error occurred when logging in",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [showToast],
  );

  const logout = useCallback(() => {
    setIsLoading(true);
    setUserToken(null);
    AsyncStorage.removeItem("userToken");
    setIsLoading(false);
  }, []);

  const isAdmin = useMemo(() => userInfo?.roleId === ROLE_ADMIN, [userInfo]);

  const isMessagingManager = useMemo(
    () => userInfo?.roleId === ROLE_MESSAGING_MANAGER,
    [userInfo],
  );
  
  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        setUserInfo,
        userToken,
        isLoading,
        userInfo,
        isAdmin,
        isMessagingManager,
        authInitializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
